// The agent's live call (tc/live/{uid}, written by the proxy from the phone's
// reports — Caller 4.30+) as words and timers for the laptop. Pure functions,
// no Firebase imports, unit-tested.
//
// Record: { state: ringing|in_call|ended|missed, direction: in|out, mobile, known,
//           customerName, startedAt, answeredAt, endedAt, durationSec, callKey,
//           sessionId?, callId?, cancelled?, updatedAt, outcome?: {callKey, by, callId, at} }

/** A ring that has not become a call within 2 min is over (the phone's end report was lost). */
export const RING_STALE_MS = 2 * 60_000;
/** A call is capped at 2 h; anything older is a lost end report. */
export const CALL_STALE_MS = 2 * 3600_000;
/** How long the "Ended" bar stays when the outcome is already saved. */
export const ENDED_SHOW_MS = 20_000;
/** How long an unsaved ended call keeps asking for its outcome. */
export const OUTCOME_WAIT_MS = 15 * 60_000;

export const mmss = (ms) => {
    const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const who = (live) => live?.customerName || (live?.mobile ? live.mobile : (live?.direction === 'out' ? 'the customer' : 'Unknown caller'));

/**
 * One view of the live record at `now` (server-ish time).
 * phase: none | ringing | connected | ended | missed
 */
export function liveView(live, now = Date.now()) {
    if (!live || !live.state) return { phase: 'none' };
    const startedAt = Number(live.startedAt) || 0;
    const answeredAt = Number(live.answeredAt) || 0;
    const endedAt = Number(live.endedAt) || 0;
    const out = live.direction === 'out';
    const name = who(live);
    const saved = !!(live.outcome && live.outcome.callKey && live.outcome.callKey === live.callKey);
    const base = { name, out, mobile: live.mobile || '', known: !!live.known, callKey: live.callKey || '', saved, savedBy: saved ? live.outcome.by : '' };

    if (live.state === 'ringing') {
        if (now - startedAt > RING_STALE_MS) return { phase: 'none', stale: true };
        return { ...base, phase: 'ringing', timerMs: now - startedAt, label: out ? `Ringing ${name}…` : `${name} calling…` };
    }
    if (live.state === 'in_call') {
        const from = answeredAt || startedAt;
        if (now - from > CALL_STALE_MS) return { phase: 'none', stale: true };
        return { ...base, phase: 'connected', timerMs: now - from, label: `Connected · ${name}` };
    }
    if (live.state === 'ended' || live.state === 'missed') {
        const age = now - (endedAt || Number(live.updatedAt) || 0);
        const keep = saved || live.cancelled ? ENDED_SHOW_MS : OUTCOME_WAIT_MS;
        if (age > keep) return { phase: 'none', stale: true };
        const talk = Number.isFinite(Number(live.durationSec)) && live.durationSec !== null
            ? Number(live.durationSec) * 1000
            : (answeredAt && endedAt ? endedAt - answeredAt : 0);
        if (live.state === 'missed') {
            return { ...base, phase: 'missed', timerMs: 0, label: `Missed call · ${name}` };
        }
        return {
            ...base, phase: 'ended', timerMs: talk,
            label: live.cancelled ? `Call not placed · ${name}` : (talk > 0 ? `Ended · ${name} · talked ${mmss(talk)}` : `Ended · ${name} · not answered`),
        };
    }
    return { phase: 'none' };
}

/** True when the outcome of THIS call (by its key) was saved on the other device. */
export function savedElsewhere(live, callKey, mine = 'web') {
    if (!live || !callKey) return false;
    const o = live.outcome;
    return !!(o && o.callKey === callKey && o.by && o.by !== mine);
}

/** "Saved on phone ✓" / "Saved on the laptop ✓" */
export const savedLabel = (by) => (by === 'phone' ? 'Saved on phone ✓' : 'Saved on the laptop ✓');

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

/**
 * The incoming-call pop-up's one line from a Customer 360 reply:
 * "last order HP8 ₹896 · follow-up due today · 1 open complaint".
 */
export function incomingSummary(c360) {
    if (!c360 || c360.found === false) return '';
    const bits = [];
    const lo = c360.lastOrder;
    if (lo) {
        const item = (lo.items || [])[0];
        const what = item ? (item.sku || item.name || '') : (lo.id || '');
        bits.push(`last order ${what}${what ? ' ' : ''}${inr(lo.amount)}`.trim());
    } else if (c360.totals && c360.totals.orders === 0) {
        bits.push('no orders yet');
    }
    const fu = c360.nextFollowup;
    if (fu) {
        const b = fu.bucket;
        bits.push(b === 'overdue' ? 'follow-up OVERDUE' : (b === 'due_now' || b === 'due_today') ? 'follow-up due today' : 'follow-up booked');
    }
    const open = Number(c360.complaints?.open) || 0;
    if (open > 0) bits.push(`${open} open complaint${open > 1 ? 's' : ''}`);
    return bits.join(' · ');
}

/** Should this record pop the incoming card (new incoming ring we have not shown)? */
export function shouldPopIncoming(live, shownKeys, now = Date.now()) {
    if (!live || live.state !== 'ringing' || live.direction !== 'in') return false;
    if (!live.callKey || shownKeys.has(live.callKey)) return false;
    return now - (Number(live.startedAt) || 0) <= RING_STALE_MS;
}
